import React, { useContext, useEffect, useState } from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import StarekContext from "../../context/StarekContext";
import { SidebarView, SidebarContextProvider } from "../../components/Sidebar";
import WebhookBasicDetails from "./WebhookBasicDetails";
import WebhookEvents from "./WebhookEvents";
import WebhookExecutionHistory from "./WebhookExecutionHistory";

const DEFAULT_PARAMETERS = { api_type: "REST", auth: { type: "NONE" }, headers: {} };

function normalizeParameters(raw) {
    if (!raw || typeof raw !== "object") return DEFAULT_PARAMETERS;
    return {
        api_type: raw.api_type ?? "REST",
        auth: raw.auth ?? { type: "NONE" },
        headers: raw.headers ?? {},
        graphql_query: raw.graphql_query ?? "",
        graphql_variables: raw.graphql_variables ?? "",
        graphql_operation_name: raw.graphql_operation_name ?? "",
    };
}

const GET_WEBHOOK = gql`
    query GetWebhook($where: WebhookWhereUniqueInput!) {
        webhook(where: $where) {
            id
            name
            description
            url
            method
            parameters
            notification_email
            is_active
            plant_id
            subscriptions {
                id
                entity_type_id
                action_mask
            }
        }
    }
`;

const CREATE_ONE_WEBHOOK = gql`
    mutation CreateOneWebhook($data: WebhookCreateInput!) {
        createOneWebhook(data: $data) {
            id
            name
            description
            url
            method
            parameters
            notification_email
            is_active
            subscriptions {
                id
                entity_type_id
                action_mask
            }
        }
    }
`;

const UPDATE_ONE_WEBHOOK = gql`
    mutation UpdateOneWebhook(
        $where: WebhookWhereUniqueInput!
        $data: WebhookUpdateInput!
    ) {
        updateOneWebhook(where: $where, data: $data) {
            id
            name
            description
            url
            method
            parameters
            notification_email
            is_active
            subscriptions {
                id
                entity_type_id
                action_mask
            }
        }
    }
`;

export const DELETE_ONE_WEBHOOK = gql`
    mutation DeleteOneWebhook($where: WebhookWhereUniqueInput!) {
        deleteOneWebhook(where: $where) {
            id
        }
    }
`;

function WebhookSidebar({ show, item, onDiscard, onSave, onDelete }) {
    const starekContext = useContext(StarekContext);
    const [preparedEntity, setPreparedEntity] = useState(null);

    const [createOneWebhook] = useMutation(CREATE_ONE_WEBHOOK, {
        client: starekContext.engineClient,
    });

    const [updateOneWebhook] = useMutation(UPDATE_ONE_WEBHOOK, {
        client: starekContext.engineClient,
    });

    const { data: webhookData, loading: loadingWebhook } = useQuery(
        GET_WEBHOOK,
        {
            client: starekContext.engineClient,
            variables: { where: { id: item?.id } },
            skip: !item?.id || !show,
            fetchPolicy: "network-only",
        },
    );

    // Prepare entity when data arrives or when opening for a new webhook
    useEffect(() => {
        if (!show) {
            setPreparedEntity(null);
            return;
        }

        if (!item?.id) {
            setPreparedEntity({
                name: "",
                description: "",
                url: "",
                method: "POST",
                parameters: DEFAULT_PARAMETERS,
                notification_email: "",
                is_active: true,
                plant_id: starekContext.lastUsedPlant?.id,
                subscriptions: [],
            });
            return;
        }

        if (webhookData?.webhook) {
            const w = webhookData.webhook;
            setPreparedEntity({
                ...w,
                parameters: normalizeParameters(w.parameters),
                notification_email: w.notification_email ?? "",
                subscriptions: w.subscriptions ?? [],
            });
        }
    }, [show, item?.id, webhookData]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSaveInternal = async (emittedData) => {
        try {
            const { name, url, subscriptions = [] } = emittedData;

            if (!name?.trim()) {
                starekContext.showError("Webhook name is required");
                return;
            }
            if (!url?.trim()) {
                starekContext.showError("Webhook URL is required");
                return;
            }

            const parameters = normalizeParameters(emittedData.parameters);
            const plantId = emittedData.plant_id || starekContext.lastUsedPlant?.id;

            const subscriptionInput = subscriptions.map((s) => ({
                entity_type: { connect: { id: Number(s.entity_type_id) } },
                action_mask: s.action_mask ?? 7,
            }));

            if (!item?.id) {
                await createOneWebhook({
                    variables: {
                        data: {
                            name: name.trim(),
                            description: emittedData.description || null,
                            url: url.trim(),
                            method: emittedData.method || "POST",
                            parameters,
                            notification_email: emittedData.notification_email || null,
                            is_active: emittedData.is_active ?? true,
                            plant: { connect: { id: Number(plantId) } },
                            created_by: { connect: { id: 1 } },
                            updated_by: { connect: { id: 1 } },
                            ...(subscriptionInput.length > 0
                                ? { subscriptions: { create: subscriptionInput } }
                                : {}),
                        },
                    },
                });
            } else {
                await updateOneWebhook({
                    variables: {
                        where: { id: Number(item.id) },
                        data: {
                            name: { set: name.trim() },
                            description: { set: emittedData.description || null },
                            url: { set: url.trim() },
                            method: { set: emittedData.method || "POST" },
                            parameters: { set: parameters },
                            notification_email: { set: emittedData.notification_email || null },
                            is_active: { set: emittedData.is_active ?? true },
                            updated_by: { connect: { id: 1 } },
                            subscriptions: {
                                deleteMany: {},
                                ...(subscriptionInput.length > 0
                                    ? { create: subscriptionInput }
                                    : {}),
                            },
                        },
                    },
                });
            }

            starekContext.showSuccess(
                item?.id ? "Webhook updated successfully" : "Webhook created successfully",
            );

            if (onSave) await onSave();
        } catch (error) {
            console.error("Error saving webhook:", error);
            starekContext.showError(error?.message || "Failed to save webhook");
            throw error;
        }
    };

    const handleDeleteInternal = async () => {
        try {
            if (onDelete) await onDelete(item);
        } catch (error) {
            console.error("Error deleting webhook:", error);
            starekContext.showError(error?.message || "Failed to delete webhook");
            throw error;
        }
    };

    if (loadingWebhook && item?.id) return null;

    return (
        <SidebarContextProvider
            entity={preparedEntity}
            show={show}
            onSave={handleSaveInternal}
            onDelete={item?.id ? handleDeleteInternal : null}
            onDiscard={onDiscard}
        >
            <SidebarView entityName="Webhook">
                <SidebarView.Page name="basic_details" title="Basic Details">
                    <WebhookBasicDetails />
                </SidebarView.Page>
                <SidebarView.Page name="events" title="Events">
                    <WebhookEvents />
                </SidebarView.Page>
                <SidebarView.Page name="execution_history" title="Execution History">
                    <WebhookExecutionHistory />
                </SidebarView.Page>
            </SidebarView>
        </SidebarContextProvider>
    );
}

export default WebhookSidebar;
